import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiList } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import {
  Notice,
  Pager,
  errorMessage,
  primaryButton,
  secondaryButton,
  selectClass,
} from '../../components/common';
import { t, type MessageKey } from '../../i18n';
import type { Delivery, Message } from './shared';

const STATUSES = ['pending', 'sending', 'sent', 'failed', 'cancelled'] as const;

export function DeliveriesTab({ schoolId }: { schoolId: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const managing = can('comms.manage', schoolId);
  const [status, setStatus] = useState('');
  const [channel, setChannel] = useState('');
  const [page, setPage] = useState(1);
  const [message, setMessage] = useState<Message>(null);

  const log = useQuery({
    queryKey: ['comms', schoolId, 'deliveries', status, channel, page],
    queryFn: () =>
      apiList<Delivery>(`/schools/${schoolId}/comms/deliveries`, {
        query: { status: status || undefined, channel: channel || undefined, page, pageSize: 20 },
      }),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['comms', schoolId] });
  const fail = (e: unknown) => setMessage({ kind: 'error', text: errorMessage(e) });
  const retry = useMutation({
    mutationFn: (id: string) =>
      api(`/schools/${schoolId}/comms/deliveries/${id}/retry`, { method: 'POST' }),
    onSuccess: () => {
      setMessage(null);
      refresh();
    },
    onError: fail,
  });
  const cancel = useMutation({
    mutationFn: (id: string) =>
      api(`/schools/${schoolId}/comms/deliveries/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      setMessage(null);
      refresh();
    },
    onError: fail,
  });
  const dispatch = useMutation({
    mutationFn: () =>
      api<{ claimed: number; sent: number; retrying: number; failed: number }>(
        `/schools/${schoolId}/comms/dispatch`,
        { method: 'POST' },
      ),
    onSuccess: (r) => {
      setMessage({
        kind: 'ok',
        text: t('cm.dispatched', { sent: r.sent, retrying: r.retrying, failed: r.failed }),
      });
      refresh();
    },
    onError: fail,
  });

  return (
    <section aria-label={t('cm.tab.deliveries')} className="space-y-4">
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label htmlFor="dl-status" className="block text-sm font-medium">
            {t('cm.statusFilter')}
          </label>
          <select
            id="dl-status"
            className={selectClass}
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
          >
            <option value="">{t('cm.any')}</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`cm.delivery.${s}` as MessageKey)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="dl-channel" className="block text-sm font-medium">
            {t('cm.channelFilter')}
          </label>
          <select
            id="dl-channel"
            className={selectClass}
            value={channel}
            onChange={(e) => {
              setChannel(e.target.value);
              setPage(1);
            }}
          >
            <option value="">{t('cm.any')}</option>
            <option value="email">{t('cm.email')}</option>
            <option value="sms">{t('cm.sms')}</option>
          </select>
        </div>
        {managing && (
          <button
            type="button"
            className={primaryButton}
            disabled={dispatch.isPending}
            onClick={() => {
              setMessage(null);
              dispatch.mutate();
            }}
          >
            {t('cm.sendQueued')}
          </button>
        )}
      </div>
      {log.data?.data.length === 0 && <p className="text-slate-600">{t('cm.noDeliveries')}</p>}
      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white text-sm">
        {log.data?.data.map((d) => (
          <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 p-3">
            <span>
              <span className="rounded bg-slate-100 px-2 py-0.5 text-xs">
                {d.channel === 'email' ? t('cm.email') : t('cm.sms')}
              </span>{' '}
              {d.toAddress}
              {d.recipientName ? ` (${d.recipientName})` : ''} ·{' '}
              <span className={d.status === 'failed' ? 'font-medium text-red-700' : ''}>
                {t(`cm.delivery.${d.status}` as MessageKey)}
              </span>
              {d.attempts > 0 ? ` · ${t('cm.attempts', { n: d.attempts })}` : ''}
              {d.lastError && d.status !== 'sent' ? ` · ${d.lastError}` : ''}
            </span>
            {managing && d.status === 'failed' && (
              <button
                type="button"
                className={secondaryButton}
                aria-label={`${t('cm.retry')}: ${d.toAddress}`}
                disabled={retry.isPending}
                onClick={() => retry.mutate(d.id)}
              >
                {t('cm.retry')}
              </button>
            )}
            {managing && d.status === 'pending' && (
              <button
                type="button"
                className={secondaryButton}
                aria-label={`${t('cm.stop')}: ${d.toAddress}`}
                disabled={cancel.isPending}
                onClick={() => cancel.mutate(d.id)}
              >
                {t('cm.stop')}
              </button>
            )}
          </li>
        ))}
      </ul>
      {log.data && log.data.meta.totalPages > 1 && (
        <Pager page={page} totalPages={log.data.meta.totalPages} onPage={setPage} />
      )}
    </section>
  );
}
